import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  alpha,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  GetApp as GetAppIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Person as PersonIcon,
  AccountCircle as AccountCircleIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import relatoriosApi from '../../services/relatoriosApi';
import ModalDetalhesSenha from './ModalDetalhesSenha';
import PDFSenhasDetalhadas from './PDFSenhasDetalhadas';
import * as XLSX from 'xlsx';

dayjs.locale('pt-br');

const TabelaSenhasDetalhadas = ({ customTheme, filtros, carregando: carregandoExterno }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState('');
  const [orderBy, setOrderBy] = useState('emitida_em');
  const [order, setOrder] = useState('desc');
  const [statusFilter, setStatusFilter] = useState('');
  const [servicoFilter, setServicoFilter] = useState('');
  const [atendenteFilter, setAtendenteFilter] = useState('');
  const [tipoFilter, setTipoFilter] = useState('');
  
  // Estados para dados
  const [senhas, setSenhas] = useState([]);
  const [servicos, setServicos] = useState([]);
  const [atendentes, setAtendentes] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  
  // Estados para o modal de detalhes
  const [modalAberto, setModalAberto] = useState(false);
  const [senhaSelecionada, setSenhaSelecionada] = useState(null);
  
  // Estado para controle de exportação
  const [exportando, setExportando] = useState(false);
  const [exportandoPdf, setExportandoPdf] = useState(false);

  // Função para buscar dados das senhas
  const buscarSenhasDetalhadas = async () => {
    try {
      setCarregando(true);
      setErro(null);
      
      // Construir filtros completos incluindo os filtros locais
      const filtrosCompletos = {
        ...filtros,
        // Adicionar filtros locais se estiverem definidos
        ...(statusFilter && { status: [statusFilter] }),
        ...(servicoFilter && { servicos: [servicoFilter] }),
        // Não enviamos filtro de atendente e tipo para o backend pois são aplicados localmente
      };
      
      console.log('[DEBUG] TabelaSenhasDetalhadas - Buscando senhas com filtros:', filtrosCompletos);
      
      const response = await relatoriosApi.buscarDadosDetalhados(filtrosCompletos);
      
      if (response.success) {
        console.log('[DEBUG] TabelaSenhasDetalhadas - Senhas recebidas:', response.data);
        setSenhas(response.data || []);
      } else {
        console.error('[DEBUG] TabelaSenhasDetalhadas - Erro na resposta:', response.message);
        setErro(response.message || 'Erro ao buscar dados das senhas');
        setSenhas([]);
      }
    } catch (error) {
      console.error('[DEBUG] TabelaSenhasDetalhadas - Erro ao buscar senhas:', error);
      setErro('Erro ao carregar dados das senhas do servidor');
      setSenhas([]);
    } finally {
      setCarregando(false);
    }
  };

  // Função para buscar serviços para o filtro
  const buscarServicos = async () => {
    try {
      const response = await relatoriosApi.buscarServicos();
      if (response.success) {
        setServicos(response.data || []);
      }
    } catch (error) {
      console.error('Erro ao buscar serviços:', error);
    }
  };

  // Função para buscar atendentes para o filtro
  const buscarAtendentes = async () => {
    try {
      const response = await relatoriosApi.buscarUsuarios();
      if (response.success && response.data) {
        // Mapear usuários para o formato esperado
        const usuariosFormatados = response.data.map(usuario => ({
          id: usuario.id,
          nome: usuario.nome
        }));
        setAtendentes(usuariosFormatados);
      } else {
        console.warn('Erro ao buscar atendentes:', response.message);
        setAtendentes([]);
      }
    } catch (error) {
      console.error('Erro ao buscar atendentes:', error);
      setAtendentes([]);
    }
  };

  // Carregar dados ao montar o componente
  useEffect(() => {
    buscarSenhasDetalhadas();
    buscarServicos();
  }, []);

  // Atualizar lista de atendentes quando as senhas mudarem
  useEffect(() => {
    if (senhas.length > 0) {
      buscarAtendentes();
    }
  }, [senhas]);

  // Atualizar dados quando os filtros mudarem
  useEffect(() => {
    if (filtros) {
      buscarSenhasDetalhadas();
    }
  }, [filtros]);

  // Atualizar dados quando filtros locais mudarem
  useEffect(() => {
    buscarSenhasDetalhadas();
  }, [statusFilter, servicoFilter]);

  // Função para formatar data/hora
  const formatarDataHora = (dataHora) => {
    if (!dataHora) return '-';
    return dayjs(dataHora).format('DD/MM/YYYY HH:mm:ss');
  };

  // Função para calcular tempo de atendimento em minutos (para ordenação)
  const calcularTempoAtendimentoMinutos = (inicioAtendimento, fimAtendimento) => {
    if (!inicioAtendimento || !fimAtendimento) return 0;
    
    const inicio = dayjs(inicioAtendimento);
    const fim = dayjs(fimAtendimento);
    return fim.diff(inicio, 'minute');
  };

  // Função para calcular tempo de atendimento formatado (para exibição)
  const calcularTempoAtendimento = (inicioAtendimento, fimAtendimento) => {
    if (!inicioAtendimento || !fimAtendimento) return '-';
    
    const inicio = dayjs(inicioAtendimento);
    const fim = dayjs(fimAtendimento);
    const duracaoSegundos = fim.diff(inicio, 'second');
    
    if (duracaoSegundos < 60) {
      return `${duracaoSegundos}seg`;
    } else {
      const minutos = Math.floor(duracaoSegundos / 60);
      const segundos = duracaoSegundos % 60;
      
      if (minutos < 60) {
        return `${minutos}min ${segundos}seg`;
      } else {
        const horas = Math.floor(minutos / 60);
        const minutosRestantes = minutos % 60;
        return `${horas}h ${minutosRestantes}min ${segundos}seg`;
      }
    }
  };

  // Função para obter cor do status
  const getStatusColor = (status) => {
    const cores = {
      'AGUARDANDO': '#2196f3',
      'CHAMADA': '#ff9800',
      'EM_ATENDIMENTO': '#9c27b0',
      'FINALIZADA': '#4caf50',
      'CANCELADA': '#f44336',
      'NAO_COMPARECEU': '#795548',
      'REABERTA': '#607d8b'
    };
    return cores[status] || '#757575';
  };

  // Função para traduzir status
  const traduzirStatus = (status) => {
    const traducoes = {
      'AGUARDANDO': 'Aguardando',
      'CHAMADA': 'Chamada',
      'EM_ATENDIMENTO': 'Em Atendimento',
      'FINALIZADA': 'Finalizada',
      'CANCELADA': 'Cancelada',
      'NAO_COMPARECEU': 'Não Compareceu',
      'REABERTA': 'Reaberta'
    };
    return traducoes[status] || status;
  };

  // Filtrar dados baseado na pesquisa e filtros locais restantes
  const dadosFiltrados = senhas.filter(senha => {
    // Filtro de busca textual
    const buscaMatch = !search || 
      senha.numero?.toString().toLowerCase().includes(search.toLowerCase()) ||
      senha.servico_nome?.toLowerCase().includes(search.toLowerCase()) ||
      senha.usuario_nome?.toLowerCase().includes(search.toLowerCase());
    
    // Filtro de atendente (aplicado localmente)
    const atendenteMatch = !atendenteFilter || senha.usuario_nome === atendenteFilter;
    
    // Filtro de tipo (aplicado localmente)
    const tipoMatch = !tipoFilter || 
      (tipoFilter === 'preferencial' && senha.preferencial) ||
      (tipoFilter === 'comum' && !senha.preferencial);
    
    return buscaMatch && atendenteMatch && tipoMatch;
  });

  // Ordenação
  const dadosOrdenados = dadosFiltrados.sort((a, b) => {
    let aValue = a[orderBy];
    let bValue = b[orderBy];
    
    // Tratamento especial para datas
    if (orderBy.includes('_em') || orderBy.includes('_at')) {
      aValue = aValue ? new Date(aValue).getTime() : 0;
      bValue = bValue ? new Date(bValue).getTime() : 0;
    }
    
    // Tratamento especial para tempo de atendimento
    if (orderBy === 'tempo_atendimento') {
      aValue = calcularTempoAtendimentoMinutos(a.inicio_atendimento, a.finalizada_em);
      bValue = calcularTempoAtendimentoMinutos(b.inicio_atendimento, b.finalizada_em);
      
      // Colocar senhas sem tempo de atendimento no final quando ordem crescente
      // e no início quando ordem decrescente
      if (aValue === 0 && bValue === 0) return 0;
      if (aValue === 0) return order === 'asc' ? 1 : -1;
      if (bValue === 0) return order === 'asc' ? -1 : 1;
    }
    
    if (order === 'asc') {
      return aValue < bValue ? -1 : 1;
    }
    return aValue > bValue ? -1 : 1;
  });

  // Paginação
  const dadosPaginados = dadosOrdenados.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Função para exportar dados para Excel
  const exportarDados = async () => {
    if (exportando) return; // Evitar múltiplas exportações simultâneas
    
    try {
      setExportando(true);
      console.log('Iniciando exportação para Excel...');
      
      // Pequeno delay para mostrar o loading
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Usar os dados filtrados que estão sendo exibidos na tabela
      const dadosParaExportar = dadosFiltrados.map((senha, index) => {
        // Formatar serviços adicionais
        const servicosAdicionaisTexto = senha.servicos_adicionais && senha.servicos_adicionais.length > 0
          ? senha.servicos_adicionais.join(', ')
          : '-';

        return {
          'Nº': index + 1,
          'Senha': senha.numero || '-',
          'Data/Hora Emissão': formatarDataHora(senha.emitida_em),
          'Data/Hora Chamada': formatarDataHora(senha.chamada_em),
          'Data/Hora Início': formatarDataHora(senha.inicio_atendimento),
          'Data/Hora Finalização': formatarDataHora(senha.finalizada_em),
          'Tempo Atendimento': calcularTempoAtendimento(senha.inicio_atendimento, senha.finalizada_em),
          'Serviço': senha.servico_nome || '-',
          'Tipo': senha.preferencial ? 'Preferencial' : 'Comum',
          'Serviço Alterado': senha.servico_alterado ? 'Sim' : 'Não',
          'Serviço Original': senha.servico_alterado ? (senha.servico_inicial || '-') : '-',
          'Status': traduzirStatus(senha.status),
          'Atendente': senha.usuario_nome || '-',
          'Guichê': senha.guiche || '-',
          'Serviços Adicionais': servicosAdicionaisTexto
        };
      });

      // Criar workbook e worksheet
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(dadosParaExportar);

      // Configurar larguras das colunas
      const columnWidths = [
        { wch: 5 },   // Nº
        { wch: 10 },  // Senha
        { wch: 18 },  // Data/Hora Emissão
        { wch: 18 },  // Data/Hora Chamada
        { wch: 18 },  // Data/Hora Início
        { wch: 18 },  // Data/Hora Finalização
        { wch: 15 },  // Tempo Atendimento
        { wch: 25 },  // Serviço
        { wch: 12 },  // Tipo
        { wch: 12 },  // Serviço Alterado
        { wch: 25 },  // Serviço Original
        { wch: 12 },  // Status
        { wch: 20 },  // Atendente
        { wch: 8 },   // Guichê
        { wch: 30 }   // Serviços Adicionais
      ];
      worksheet['!cols'] = columnWidths;

      // Adicionar worksheet ao workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Senhas Detalhadas');

      // Gerar nome do arquivo com data/hora atual
      const agora = new Date();
      const dataFormatada = agora.toLocaleDateString('pt-BR').replace(/\//g, '-');
      const horaFormatada = agora.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }).replace(/:/g, '-');
      const nomeArquivo = `relatorio-senhas-detalhadas_${dataFormatada}_${horaFormatada}.xlsx`;

      // Fazer download do arquivo
      XLSX.writeFile(workbook, nomeArquivo);

      console.log(`Exportação concluída: ${dadosParaExportar.length} registros exportados para ${nomeArquivo}`);
      
      // Feedback visual opcional (você pode adicionar um snackbar/toast aqui)
      alert(`✅ Exportação concluída!\n${dadosParaExportar.length} registros exportados para:\n${nomeArquivo}`);
      
    } catch (error) {
      console.error('Erro ao exportar dados:', error);
      alert('❌ Erro ao exportar dados. Tente novamente.');
    } finally {
      setExportando(false);
    }
  };

  // Função para exportar PDF
  const exportarPDF = async () => {
    try {
      setExportandoPdf(true);
      
      console.log('[DEBUG] TabelaSenhasDetalhadas - Exportando PDF com dados:', {
        totalSenhas: dadosFiltrados.length,
        filtros
      });
      
      // Usar os dados filtrados para o PDF
      PDFSenhasDetalhadas.gerarPDF({
        senhas: dadosFiltrados,
        filtros: {
          dataInicio: filtros.dataInicio ? dayjs(filtros.dataInicio).format('DD/MM/YYYY') : 'N/A',
          dataFim: filtros.dataFim ? dayjs(filtros.dataFim).format('DD/MM/YYYY') : 'N/A',
          ...filtros
        },
        onSuccess: (mensagem) => {
          console.log('[DEBUG] TabelaSenhasDetalhadas - PDF gerado com sucesso:', mensagem);
          setExportandoPdf(false);
        },
        onError: (erro) => {
          console.error('[DEBUG] TabelaSenhasDetalhadas - Erro ao gerar PDF:', erro);
          alert('Erro ao gerar PDF: ' + erro);
          setExportandoPdf(false);
        }
      });
      
    } catch (error) {
      console.error('[DEBUG] TabelaSenhasDetalhadas - Erro na exportação PDF:', error);
      alert('Erro ao exportar PDF: ' + error.message);
      setExportandoPdf(false);
    }
  };

  // Função para abrir modal de detalhes
  const abrirModalDetalhes = (senha) => {
    setSenhaSelecionada(senha);
    setModalAberto(true);
  };

  // Função para fechar modal de detalhes
  const fecharModalDetalhes = () => {
    setModalAberto(false);
    setSenhaSelecionada(null);
  };

  const statusOptions = [
    'AGUARDANDO',
    'CHAMADA', 
    'EM_ATENDIMENTO',
    'FINALIZADA',
    'CANCELADA',
    'NAO_COMPARECEU',
    'REABERTA'
  ];

  if (carregando || carregandoExterno) {
    return (
      <Paper
        sx={{
          borderRadius: 3,
          border: `1px solid ${alpha(customTheme.palette.primary.main, 0.1)}`,
          p: 4,
          textAlign: 'center'
        }}
      >
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Carregando dados das senhas...
        </Typography>
      </Paper>
    );
  }

  if (erro) {
    return (
      <Paper
        sx={{
          borderRadius: 3,
          border: `1px solid ${alpha(customTheme.palette.primary.main, 0.1)}`,
          p: 4
        }}
      >
        <Alert severity="error">{erro}</Alert>
      </Paper>
    );
  }

  return (
    <Paper
      sx={{
        borderRadius: 3,
        border: `1px solid ${alpha(customTheme.palette.primary.main, 0.1)}`,
        overflow: 'hidden'
      }}
    >
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Relatório Detalhado de Senhas
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Visualização completa de todas as senhas com informações detalhadas de atendimento
        </Typography>

        {/* Barra de ferramentas */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
          <TextField
            placeholder="Pesquisar por número, serviço ou atendente..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 300 }}
          />
          
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">Todos</MenuItem>
              {statusOptions.map((status) => (
                <MenuItem key={status} value={status}>
                  {traduzirStatus(status)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Serviço</InputLabel>
            <Select
              value={servicoFilter}
              label="Serviço"
              onChange={(e) => setServicoFilter(e.target.value)}
            >
              <MenuItem value="">Todos</MenuItem>
              {servicos.map((servico) => (
                <MenuItem key={servico.id} value={servico.nome}>
                  {servico.nome}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Tipo</InputLabel>
            <Select
              value={tipoFilter}
              label="Tipo"
              onChange={(e) => setTipoFilter(e.target.value)}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="comum">Comum</MenuItem>
              <MenuItem value="preferencial">Preferencial</MenuItem>
            </Select>
          </FormControl>
          
          <Autocomplete
            size="small"
            sx={{ minWidth: 250 }}
            options={atendentes}
            getOptionLabel={(option) => option.nome || ''}
            value={atendentes.find(a => a.nome === atendenteFilter) || null}
            onChange={(event, newValue) => {
              setAtendenteFilter(newValue ? newValue.nome : '');
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Atendente"
                placeholder="Digite ou selecione..."
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    </InputAdornment>
                  )
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box
                component="li"
                {...props}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  py: 1,
                  '&:hover': {
                    bgcolor: alpha(customTheme.palette.primary.main, 0.05)
                  }
                }}
              >
                <PersonIcon sx={{ fontSize: 16, color: customTheme.palette.primary.main }} />
                <Typography variant="body2">{option.nome}</Typography>
              </Box>
            )}
            noOptionsText="Nenhum atendente encontrado"
            clearOnEscape
            selectOnFocus
            handleHomeEndKeys
            freeSolo={false}
            autoHighlight
            blurOnSelect
          />
          
          <Box sx={{ flexGrow: 1 }} />
          
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              size="small"
              startIcon={exportandoPdf ? <CircularProgress size={16} /> : <GetAppIcon />}
              onClick={exportarPDF}
              disabled={exportandoPdf || dadosFiltrados.length === 0}
              sx={{ 
                borderRadius: 2,
                minWidth: 140,
                borderColor: '#d32f2f',
                color: '#d32f2f',
                '&:hover': {
                  borderColor: '#b71c1c',
                  backgroundColor: alpha('#d32f2f', 0.04)
                },
                '&:disabled': {
                  opacity: 0.6
                }
              }}
            >
              {exportandoPdf ? 'Gerando...' : 'Exportar PDF'}
            </Button>
            
            <Button
              variant="outlined"
              size="small"
              startIcon={exportando ? <CircularProgress size={16} /> : <GetAppIcon />}
              onClick={exportarDados}
              disabled={exportando || dadosFiltrados.length === 0}
              sx={{ 
                borderRadius: 2,
                minWidth: 140,
                '&:disabled': {
                  opacity: 0.6
                }
              }}
            >
              {exportando ? 'Exportando...' : 'Exportar Excel'}
            </Button>
          </Stack>
        </Stack>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: alpha(customTheme.palette.primary.main, 0.05) }}>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'numero'}
                  direction={orderBy === 'numero' ? order : 'asc'}
                  onClick={() => handleSort('numero')}
                  sx={{ fontWeight: 600 }}
                >
                  Senha
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'emitida_em'}
                  direction={orderBy === 'emitida_em' ? order : 'asc'}
                  onClick={() => handleSort('emitida_em')}
                  sx={{ fontWeight: 600 }}
                >
                  Data/Hora Emissão
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'chamada_em'}
                  direction={orderBy === 'chamada_em' ? order : 'asc'}
                  onClick={() => handleSort('chamada_em')}
                  sx={{ fontWeight: 600 }}
                >
                  Data/Hora Chamada
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'inicio_atendimento'}
                  direction={orderBy === 'inicio_atendimento' ? order : 'asc'}
                  onClick={() => handleSort('inicio_atendimento')}
                  sx={{ fontWeight: 600 }}
                >
                  Data/Hora Início
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'finalizada_em'}
                  direction={orderBy === 'finalizada_em' ? order : 'asc'}
                  onClick={() => handleSort('finalizada_em')}
                  sx={{ fontWeight: 600 }}
                >
                  Data/Hora Finalização
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'tempo_atendimento'}
                  direction={orderBy === 'tempo_atendimento' ? order : 'asc'}
                  onClick={() => handleSort('tempo_atendimento')}
                  sx={{ fontWeight: 600 }}
                >
                  Tempo Atendimento
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'servico_nome'}
                  direction={orderBy === 'servico_nome' ? order : 'asc'}
                  onClick={() => handleSort('servico_nome')}
                  sx={{ fontWeight: 600 }}
                >
                  Serviço
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'preferencial'}
                  direction={orderBy === 'preferencial' ? order : 'asc'}
                  onClick={() => handleSort('preferencial')}
                  sx={{ fontWeight: 600 }}
                >
                  Tipo
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'status'}
                  direction={orderBy === 'status' ? order : 'asc'}
                  onClick={() => handleSort('status')}
                  sx={{ fontWeight: 600 }}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'usuario_nome'}
                  direction={orderBy === 'usuario_nome' ? order : 'asc'}
                  onClick={() => handleSort('usuario_nome')}
                  sx={{ fontWeight: 600 }}
                >
                  Atendente
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Serviços Adicionais</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dadosPaginados.map((senha, index) => (
              <TableRow 
                key={senha.id || index}
                hover
                sx={{ 
                  '&:hover': { 
                    bgcolor: alpha(customTheme.palette.primary.main, 0.02) 
                  } 
                }}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {senha.numero || '-'}
                    </Typography>
                    {senha.usuario_emitiu && (
                      <Tooltip 
                        title={`Emitida por usuário${senha.usuario_emissor_nome ? `: ${senha.usuario_emissor_nome}` : ''}`}
                        placement="top"
                      >
                        <AccountCircleIcon 
                          sx={{ 
                            fontSize: 16, 
                            color: customTheme.palette.info.main,
                            opacity: 0.7,
                            cursor: 'help'
                          }} 
                        />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatarDataHora(senha.emitida_em)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatarDataHora(senha.chamada_em)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatarDataHora(senha.inicio_atendimento)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatarDataHora(senha.finalizada_em)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 600,
                      color: senha.inicio_atendimento && senha.finalizada_em ? 
                        customTheme.palette.success.main : 'text.secondary'
                    }}
                  >
                    {calcularTempoAtendimento(senha.inicio_atendimento, senha.finalizada_em)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {senha.servico_nome || '-'}
                  </Typography>
                  {senha.servico_alterado && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      (Alterado de: {senha.servico_inicial || 'N/A'})
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={senha.preferencial ? 'Preferencial' : 'Comum'}
                    size="small"
                    sx={{
                      bgcolor: senha.preferencial 
                        ? alpha('#ff9800', 0.1) 
                        : alpha('#2196f3', 0.1),
                      color: senha.preferencial ? '#ff9800' : '#2196f3',
                      fontWeight: 600
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={traduzirStatus(senha.status)}
                    size="small"
                    sx={{
                      bgcolor: alpha(getStatusColor(senha.status), 0.1),
                      color: getStatusColor(senha.status),
                      fontWeight: 600
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {senha.usuario_nome || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  {senha.servicos_adicionais && senha.servicos_adicionais.length > 0 ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 200 }}>
                      {senha.servicos_adicionais.slice(0, 2).map((servico, idx) => (
                        <Chip
                          key={idx}
                          label={servico}
                          size="small"
                          variant="outlined"
                          sx={{
                            fontSize: '0.75rem',
                            height: 20,
                            borderColor: alpha(customTheme.palette.secondary.main, 0.3),
                            color: customTheme.palette.secondary.main,
                            '& .MuiChip-label': {
                              px: 1
                            }
                          }}
                        />
                      ))}
                      {senha.servicos_adicionais.length > 2 && (
                        <Tooltip 
                          title={senha.servicos_adicionais.slice(2).join(', ')}
                          placement="top"
                        >
                          <Chip
                            label={`+${senha.servicos_adicionais.length - 2}`}
                            size="small"
                            sx={{
                              fontSize: '0.75rem',
                              height: 20,
                              bgcolor: alpha(customTheme.palette.info.main, 0.1),
                              color: customTheme.palette.info.main,
                              fontWeight: 600
                            }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      -
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Tooltip title="Ver detalhes">
                    <IconButton
                      size="small"
                      onClick={() => abrirModalDetalhes(senha)}
                      sx={{ color: customTheme.palette.primary.main }}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            
            {dadosPaginados.length === 0 && (
              <TableRow>
                <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    Nenhuma senha encontrada com os filtros aplicados
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={dadosFiltrados.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50, 100]}
        labelRowsPerPage="Linhas por página:"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
        }
        sx={{
          borderTop: `1px solid ${alpha(customTheme.palette.primary.main, 0.1)}`,
          bgcolor: alpha(customTheme.palette.primary.main, 0.02),
        }}
      />
      
      {/* Modal de Detalhes da Senha */}
      <ModalDetalhesSenha
        open={modalAberto}
        onClose={fecharModalDetalhes}
        senha={senhaSelecionada}
        customTheme={customTheme}
      />
    </Paper>
  );
};

export default TabelaSenhasDetalhadas;